import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from './config';
import Header from './components/Header';
import apiService from './services/apiService';

function SimilarMandatesPage() {
  const { mandateId } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMandate, setSelectedMandate] = useState(null);
  const [linkedCandidates, setLinkedCandidates] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [linkedError, setLinkedError] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [linkedMandates, setLinkedMandates] = useState([]);
  const [loadingLinkedMandates, setLoadingLinkedMandates] = useState(false);
  const [linkedMandatesError, setLinkedMandatesError] = useState('');
  const navigate = useNavigate();

  // For heading: get the details of the reference mandate
  const [referenceMandate, setReferenceMandate] = useState(null);

  useEffect(() => {
    async function fetchSimilar() {
      setLoading(true);
      try {
        const data = await apiService.getSimilarMandates(mandateId, 20);
        setResults(data);

        // Fetch the reference mandate's details for the heading
        try {
          const refData = await apiService.getMandateDetails(mandateId);
          setReferenceMandate(refData);
        } catch (refError) {
          if (data && data.results && data.results.length > 0) {
            // Fallback to using data from similar results
            setReferenceMandate({ id: mandateId, job_title: data.results[0].job_title });
          } else {
            setReferenceMandate({ id: mandateId, job_title: 'Unknown Mandate' });
          }
        }
      } catch (err) {
        console.error('Error fetching similar mandates:', err);
        setReferenceMandate({ id: mandateId, job_title: 'Unknown Mandate' });
      } finally {
        setLoading(false);
      }
    }
    fetchSimilar();
  }, [mandateId]);

  // Handle opening mandate modal with linked candidates
  const handleOpenMandateModal = async (mandate) => {
    setSelectedMandate(mandate);
    setLinkedCandidates([]);
    setLinkedError('');
    setLoadingLinked(true);
    try {
      const data = await apiService.getMandateLinkedCandidates(mandate.id);
      setLinkedCandidates(data.candidates);
    } catch (err) {
      setLinkedError(`Failed to fetch linked candidates: ${err.message}`);
    } finally {
      setLoadingLinked(false);
    }
  };

  // Modal close handler
  const closeModal = () => {
    setSelectedMandate(null);
    setLinkedCandidates([]);
    setLinkedError('');
  };

  const fetchCandidateDetails = async (candidateId) => {
    try {
      const data = await apiService.getCandidateDetails(candidateId);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const handleOpenCandidateModal = async (candidate) => {
    setSelectedCandidate(candidate);
    setLinkedMandates([]);
    setLinkedMandatesError('');
    setLoadingLinkedMandates(true);
    try {
      const data = await apiService.getCandidateLinkedMandates(candidate.id);
      setLinkedMandates(data.mandates);
    } catch (err) {
      setLinkedMandatesError(`Failed to fetch linked mandates: ${err.message}`);
    } finally {
      setLoadingLinkedMandates(false);
    }
  };

  const closeCandidateModal = () => {
    setSelectedCandidate(null);
    setLinkedMandates([]);
    setLinkedMandatesError('');
  };

  return (
    <div className="App">
      <div className="container">
        <Header showBackButton={true} onBackClick={() => navigate(-1)} />
        
        {/* Reference Mandate Details Section */}
        {referenceMandate && (
          <div className="mandate-section" style={{ background: "#f8f9fa", borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16 }}>📄 Finding Similar Mandates for</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%" }}>
              <div><strong>ID:</strong> {referenceMandate.id}</div>
              <div><strong>Job Title:</strong> {referenceMandate.job_title || 'Not specified'}</div>
              <div><strong>Location:</strong> {referenceMandate.location || 'Not specified'}</div>
              <div><strong>Primary Skills:</strong> {referenceMandate.primary_skills || 'Not specified'}</div>
              <div><strong>Secondary Skills:</strong> {referenceMandate.secondary_skills || 'Not specified'}</div>
              <div><strong>CTC Range:</strong> {referenceMandate.ctc_range_start && referenceMandate.ctc_range_end ? `${referenceMandate.ctc_range_start} - ${referenceMandate.ctc_range_end} LPA` : 'Not specified'}</div>
              <div><strong>Experience Range:</strong> {referenceMandate.exp_range_start && referenceMandate.exp_range_end ? `${referenceMandate.exp_range_start} - ${referenceMandate.exp_range_end} years` : 'Not specified'}</div>
              {referenceMandate.job_desc && (
                <div style={{ width: "100%" }}>
                  <strong>Job Description:</strong>
                  <div 
                    style={{ background: "#fff", borderRadius: 8, padding: 12, marginTop: 4 }}
                    dangerouslySetInnerHTML={{ __html: referenceMandate.job_desc }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {loading && <div>Loading...</div>}
        {results && (
          <div className="results-section">
            <div className="results-header">
              <h2>🎯 Similar Mandates</h2>
              <div className="results-stats">
                <span className="stat">📈 {results.results?.length || 0} similar mandates found</span>
              </div>
            </div>
            <div className="candidates-table-wrapper">
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Job Title</th>
                    <th>Location</th>
                    <th>Primary Skills</th>
                    <th>CTC Range</th>
                    <th>Experience Range</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((mandate) => (
                    <tr
                      key={mandate.id}
                      className="candidate-row"
                      onClick={() => handleOpenMandateModal(mandate)}
                      style={{ cursor: 'pointer' }}
                      tabIndex={0}
                      aria-label={`View details for ${mandate.job_title || mandate.id}`}
                    >
                      <td>{mandate.id}</td>
                      <td>{mandate.job_title || '-'}</td>
                      <td>{mandate.location || '-'}</td>
                      <td>{mandate.primary_skills || '-'}</td>
                      <td>{mandate.ctc_range_start && mandate.ctc_range_end ? `${mandate.ctc_range_start} - ${mandate.ctc_range_end} LPA` : '-'}</td>
                      <td>{mandate.exp_range_start && mandate.exp_range_end ? `${mandate.exp_range_start} - ${mandate.exp_range_end} years` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Modal for mandate details */}
            {selectedMandate && (
              <div className="modal-overlay" onClick={closeModal}>
                <div
                  className="modal-content"
                  onClick={e => e.stopPropagation()}
                  tabIndex={-1}
                  style={{ maxWidth: 600 }}
                >
                  <button className="modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                  <h2>
                    {selectedMandate.job_title ? (
                      <>
                        <span className="modal-name">{selectedMandate.job_title}</span>
                        <span className="modal-id"> (ID: {selectedMandate.id})</span>
                      </>
                    ) : (
                      <>ID: {selectedMandate.id}</>
                    )}
                  </h2>
                  <div className="modal-fields">
                    <div className="field-row">
                      <span className="label">📍 Location</span>
                      <span className="value">{selectedMandate.location || 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">🛠️ Primary Skills</span>
                      <span className="value">{selectedMandate.primary_skills || 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">🎯 Secondary Skills</span>
                      <span className="value">{selectedMandate.secondary_skills || 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">💰 CTC Range</span>
                      <span className="value">{selectedMandate.ctc_range_start && selectedMandate.ctc_range_end ? `${selectedMandate.ctc_range_start} - ${selectedMandate.ctc_range_end} LPA` : 'Not specified'}</span>
                    </div>
                    <div className="field-row">
                      <span className="label">🧑‍💼 Experience Range</span>
                      <span className="value">{selectedMandate.exp_range_start && selectedMandate.exp_range_end ? `${selectedMandate.exp_range_start} - ${selectedMandate.exp_range_end} years` : 'Not specified'}</span>
                    </div>
                    {selectedMandate.job_desc && (
                      <div className="field-row">
                        <span className="label">📝 Job Description</span>
                        <div 
                          className="value" 
                          style={{ background: "#f8f9fa", borderRadius: 8, padding: 12, marginTop: 4 }}
                          dangerouslySetInnerHTML={{ __html: selectedMandate.job_desc }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginTop: 16 }}>
                    <strong>Linked Candidates:</strong>
                    {loadingLinked && <div>Loading...</div>}
                    {linkedError && <div className="error-message">{linkedError}</div>}
                    {(!loadingLinked && linkedCandidates.length === 0 && !linkedError) && (
                      <div>No linked candidates found.</div>
                    )}
                    {linkedCandidates.length > 0 && (
                      <table className="candidates-table" style={{ marginTop: 8 }}>
                        <thead>
                          <tr>
                            <th>Candidate ID</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>View</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linkedCandidates.map((c) => (
                            <tr key={c.candidate_id}>
                              <td>{c.candidate_id}</td>
                              <td>{c.name || '-'}</td>
                              <td>{c.status_name}</td>
                              <td>
                                <button
                                  onClick={async () => {
                                    try {
                                      const fullDetails = await fetchCandidateDetails(c.candidate_id);
                                      await handleOpenCandidateModal(fullDetails);
                                    } catch (err) {
                                      alert("Failed to fetch candidate details: " + err.message);
                                    }
                                  }}
                                  style={{
                                    background: "#007bff",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "4px 8px",
                                    fontSize: "0.9em",
                                    cursor: "pointer",
                                    fontWeight: "bold"
                                  }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  
                  {/* Search Candidates Button */}
                  <button
                    type="button"
                    style={{
                      marginTop: 16,
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      closeModal();
                      navigate(`/?search=${selectedMandate.id}`);
                    }}
                  >
                    🔍 Search Candidates for this Mandate
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal for candidate details */}
        {selectedCandidate && (
          <div className="modal-overlay" onClick={closeCandidateModal} style={{ zIndex: 9999 }}>
            <div
              className="modal-content"
              onClick={e => e.stopPropagation()}
              tabIndex={-1}
              style={{ zIndex: 10000 }}
            >
              <button className="modal-close" onClick={closeCandidateModal} aria-label="Close">&times;</button>
              <h2>
                {selectedCandidate.name ? (
                  <>
                    <span className="modal-name">{selectedCandidate.name}</span>
                    <span className="modal-id"> (ID: <a 
                      href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#007bff',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                      title="Open candidate in external application"
                    >
                      {selectedCandidate.id}
                    </a>)</span>
                  </>
                ) : (
                  <>ID: <a 
                    href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#007bff',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                    title="Open candidate in external application"
                  >
                    {selectedCandidate.id}
                  </a></>
                )}
              </h2>
              <div className="modal-fields">
                <div className="field-row">
                  <span className="label">📍 Location</span>
                  <span className="value">{selectedCandidate.location || 'Not specified'}</span>
                </div>
                <div className="field-row">
                  <span className="label">💰 CTC</span>
                  <span className="value">{selectedCandidate.ctc ? `${selectedCandidate.ctc} LPA` : 'Not specified'}</span>
                </div>
                <div className="field-row">
                  <span className="label">🧑‍💼 Experience</span>
                  <span className="value">{selectedCandidate.experience ? `${selectedCandidate.experience} years` : 'Not specified'}</span>
                </div>
                <div className="field-row">
                  <span className="label">🛠️ Key Skills</span>
                  <span className="value">{selectedCandidate.key_skills || 'Not specified'}</span>
                </div>
                {selectedCandidate.other_skills && (
                  <div className="field-row">
                    <span className="label">🎯 Other Skills</span>
                    <span className="value">{selectedCandidate.other_skills}</span>
                  </div>
                )}
                {selectedCandidate.work_summary && (
                  <div className="field-row">
                    <span className="label">📝 Summary</span>
                    <span className="value">{selectedCandidate.work_summary}</span>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 16 }}>
                <strong>Linked Mandates:</strong>
                {loadingLinkedMandates && <div>Loading...</div>}
                {linkedMandatesError && <div className="error-message">{linkedMandatesError}</div>}
                {(!loadingLinkedMandates && linkedMandates.length === 0 && !linkedMandatesError) && (
                  <div>Not linked to any other mandates.</div>
                )}
                {linkedMandates.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {linkedMandates.map((m) => (
                      <li key={m.mandate_id}>
                        <span style={{ fontWeight: "bold" }}>{m.mandate_id}</span>
                        {m.job_title && <> – <span>{m.job_title}</span></>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                style={{
                  marginTop: 16,
                  background: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  closeCandidateModal();
                  navigate(`/similar-candidates/${selectedCandidate.id}`);
                }}
              >
                Find Similar Candidates
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SimilarMandatesPage; 